import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
        <p className="text-gray-400 text-sm mb-8">Last updated: March 18, 2026</p>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              This Privacy Policy describes how deepnets.ai, operated by Percipio LLC ("we", "us", or "our"),
              collects, uses, and protects information when you use our website and services (the "Service").
              We are committed to protecting your privacy and being transparent about what data we collect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">Blockchain Data</h3>
            <p>
              We analyze publicly available on-chain data from the Solana blockchain, including wallet addresses,
              token transactions, holder distributions, and funding relationships. This data is inherently public
              and accessible to anyone on the blockchain.
            </p>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">Account Information</h3>
            <p>
              If you create an account, we collect your wallet address for authentication via Solana wallet signature.
              We do not collect email addresses, names, or other personal information during account creation.
            </p>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">Usage Data</h3>
            <p>
              We collect anonymized usage data including page views, API requests, and general traffic patterns
              to understand how the Service is used and to improve it. This data is not linked to individual users.
            </p>

            <h3 className="text-lg font-medium text-gray-200 mt-4 mb-2">API Keys</h3>
            <p>
              If you use our public API, we issue and store API keys associated with your account.
              API usage (request counts, endpoints accessed) is logged for rate limiting and abuse prevention.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Bot Protection</h2>
            <p>
              We use{' '}
              <a
                href="https://www.cloudflare.com/products/turnstile/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Cloudflare Turnstile
              </a>{' '}
              to protect our services from automated abuse. Turnstile may collect interaction data,
              browser characteristics, and cookies to distinguish humans from bots. This data is processed
              by Cloudflare in accordance with their{' '}
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Privacy Policy
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Analytics</h2>
            <p>
              We may use privacy-focused analytics services to understand traffic patterns and usage.
              These services do not use cookies for tracking and do not collect personally identifiable information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Cookies</h2>
            <p>
              We use minimal cookies necessary for the operation of the Service, including session tokens
              for authenticated users. We do not use advertising or third-party tracking cookies.
              Cloudflare Turnstile may set cookies as part of its bot detection functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share data with:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
              <li>Cloudflare — for bot protection and content delivery</li>
              <li>Infrastructure providers — for hosting and service delivery</li>
              <li>Law enforcement — when required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Data Retention</h2>
            <p>
              Usage logs are retained for a limited period for operational purposes and then deleted.
              Account data is retained as long as your account is active. Blockchain data analysis results
              are retained to provide the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Security</h2>
            <p>
              We implement reasonable security measures to protect information, including encrypted
              connections (HTTPS), signed session tokens, and rate limiting. However, no method of
              transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Your Rights</h2>
            <p>
              You may request deletion of your account and associated data by contacting us.
              Since the Service primarily analyzes publicly available blockchain data, we cannot
              delete on-chain data that exists on the Solana blockchain.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be reflected by updating
              the "Last updated" date at the top of this page. Your continued use of the Service after
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Contact</h2>
            <p>
              If you have questions about this Privacy Policy, please reach out via our{' '}
              <a
                href="https://x.com/i/chat/group_join/g2050256478343213064/QYTP6iN6Q6"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                XChat group
              </a>{' '}
              or contact us at{' '}
              <a href="mailto:privacy@deepnets.ai" className="text-blue-400 hover:text-blue-300">
                privacy@deepnets.ai
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <Link to="/terms" className="text-blue-400 hover:text-blue-300">
            Terms of Service →
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
